import {
    type ChangeEvent,
    type FormEvent,
    useCallback,
    useEffect,
    useState,
} from "react";
import {
    Button,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";
import CreatableSelect from "react-select/creatable";
import makeAnimated from "react-select/animated";

import styles from "Pages/Admin/CompanySetup/CompanySetupModal/CompanySetupModal.module.css";
import useHelpers from "Hooks/useHelpers";
import useFetch from "Hooks/useFetch";
import useUserContext from "Hooks/useUserContext";
import useAlertContext from "Hooks/useAlertContext";
import Slider from "components/Slider/Slider";
import type { ActionType } from "Pages/Admin/CompanySetup/CompanySetup";
import DimensionSpinner from "components/DimensionSpinner/DimensionSpinner";

interface IProps {
    activeModal: ActionType;
    getCompanyInfo: (ignore: boolean) => Promise<void>;
    onSetActiveModal: (modal: ActionType) => void;
    onSetSelectedRow: (row: any) => void;
    selectedRow: any;
}

interface ISystems {
    id: number;
    code: string;
    name: string;
}

interface IOption {
    value: string;
    label: string;
}

const defaultFormData = {
    company_id: "",
    name: "",
    enabled: true,
    systems: [] as IOption[],
    status: "",
    address: "",
    contact_emails: "",
    notes: "",
};

export default function CompanySetupModal({
    activeModal,
    getCompanyInfo,
    onSetActiveModal,
    onSetSelectedRow,
    selectedRow,
}: IProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [formData, setFormData] = useState(defaultFormData);
    const [systems, setSystems] = useState<ISystems[]>([]);
    const [userContext] = useUserContext();

    /** Hook for react-select inputs to make them animated when deleting */
    const animated = makeAnimated();

    const { newAlert } = useAlertContext();

    const { handleFormChange } = useHelpers();
    const { get, post } = useFetch();

    const closeModal = useCallback(() => {
        onSetActiveModal("");
        setErrorMessage("");
        setFormData(defaultFormData);
    }, [onSetActiveModal]);

    const getSystems = useCallback(async () => {
        const response = await get("/api/admin/system");

        let data: Response | ISystems[];
        try {
            data = await response.json();
        } catch (e) {
            data = response;
        }
        if (response.ok) {
            data = data as ISystems[];
            setSystems(data);
        }
    }, [get]);

    // Fetch systems on component mount
    useEffect(() => {
        if (systems.length > 0) return;

        getSystems();
    }, [getSystems, systems.length]);

    // Handle alert for no row selected, prefill inputs conditionally
    useEffect(() => {
        if (activeModal === "new") setFormData(defaultFormData);

        // Handle no row selected for edit and delete
        if (activeModal === "edit" && !selectedRow) {
            closeModal();
            newAlert({
                message: "No entry selected",
                color: "danger",
            });
            return;
        }

        // Set formData to selected row values so values appear in inputs
        if (activeModal !== "new") {
            const reactSelectSystemValues = selectedRow?.values.systems
                ?.split("|")
                .map((system: IOption) => ({
                    value: system,
                    label: system,
                }));

            setFormData({
                company_id: selectedRow ? selectedRow.values.company_id : "",
                name: selectedRow ? selectedRow.values.name : "",
                enabled: selectedRow ? selectedRow.values.enabled : false,
                systems: selectedRow
                    ? reactSelectSystemValues
                    : ([] as IOption[]),
                status: selectedRow ? selectedRow.values.status : "",
                address: selectedRow ? selectedRow.values.address : "",
                contact_emails: selectedRow
                    ? selectedRow.values.contact_emails
                    : "",
                notes: selectedRow ? selectedRow.values.notes : "",
            });
        }
    }, [activeModal, closeModal, newAlert, selectedRow]);

    async function submitFormData(e: FormEvent) {
        e.preventDefault();

        setIsLoading(true);

        const url = `/api/admin/company/${activeModal}/${userContext.companyId}`;
        console.warn(formData);
        const systemsAsStringWithPipeSeparator = formData.systems
            ?.map((system) => system.value)
            .join("|");

        const body = {
            company_id: formData.company_id,
            name: formData.name,
            enabled: formData.enabled,
            systems: systemsAsStringWithPipeSeparator,
            status: formData.status,
            address: formData.address,
            contact_emails: formData.contact_emails,
            notes: formData.notes,
        };

        const response = await post(url, body);
        const data = await response.json();
        console.log(data);

        // If the company already exists, stop
        if (
            data.detail === "Company already exists." ||
            data.detail ===
                "Failed to edit configuration: Sequence contains more than one element"
        ) {
            setErrorMessage("Company already exists.");
        }
        // Otherwise proceed as normal
        else {
            closeModal();

            if (response.ok) {
                // Show the alert
                newAlert({
                    message: "Request submitted successfully",
                    color: "success",
                });
                // Erase the selected row state
                onSetSelectedRow(undefined);
                // Fetch the company configurations again from the parent component to update the table
                getCompanyInfo(false);
                // update systems if a new one was created
                getSystems();
            } else {
                newAlert({
                    message: "Error, request failed",
                });
            }
        }

        setIsLoading(false);
    }

    let headerContent = "";
    switch (activeModal) {
        case "new":
            headerContent = "Create new entry";
            break;
        case "edit":
            headerContent = "Edit entry";
            break;
    }

    const handleEnabledSliderClick = (e: ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, enabled: e.target.checked });
    };

    /* The systems property becomes an array of IOption objects due to using 
    the react-select input library. formData.systems looks like:
    [
        { value: "system1", label: "System 1" },
        { value: "system2", label: "System 2" },
    ]
    */
    const handleSelectChange = (newValue: IOption[], key: string) => {
        setFormData({ ...formData, [key]: newValue });
    };

    const systemOptions: IOption[] = systems.map((system) => ({
        value: system.code,
        label: system.name,
    }));

    const bodyContent = (
        <Form id="form" onSubmit={submitFormData}>
            {/* Company ID */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label htmlFor="company_id">Company ID:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        inputId="company_id"
                        name="company_id"
                        className={styles.input}
                        minLength={3}
                        maxLength={100}
                        required
                        onChange={(e) => handleFormChange(e, setFormData)}
                        value={formData.company_id}
                        disabled={activeModal === "edit"}
                    />
                </Col>
            </FormGroup>

            {/* Name */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label htmlFor="name">Name:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        inputId="name"
                        name="name"
                        className={styles.input}
                        minLength={3}
                        maxLength={100}
                        required
                        onChange={(e) => handleFormChange(e, setFormData)}
                        value={formData.name}
                    />
                </Col>
            </FormGroup>

            {/* Enabled */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label htmlFor="enabled">Enabled: </Label>
                </Col>
                <Col sm={2}>
                    <Slider
                        id="enabled"
                        checked={formData.enabled}
                        onChange={(e) => handleEnabledSliderClick(e)}
                        size={0.5}
                    />
                </Col>
            </FormGroup>

            {/* Systems */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label htmlFor="systems">Systems:</Label>
                </Col>
                <Col sm={10}>
                    <CreatableSelect
                        inputId="systems"
                        name="systems"
                        className={styles.input}
                        closeMenuOnSelect={false}
                        components={animated}
                        isMulti
                        options={systemOptions}
                        onChange={(value: any) =>
                            handleSelectChange(value, "systems")
                        }
                        value={formData.systems}
                    />
                </Col>
            </FormGroup>

            {/* Status */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label htmlFor="status">Status:</Label>
                </Col>
                <Col sm={10}>
                    {["live", "uat", "closed", "na", "dormant"].map(
                        (status) => (
                            <div key={status}>
                                <Input
                                    type="radio"
                                    inputId={status}
                                    name="status"
                                    className={styles.input}
                                    value={status.toLowerCase()}
                                    onChange={(e) =>
                                        handleFormChange(e, setFormData)
                                    }
                                    checked={
                                        formData.status === status.toLowerCase()
                                    }
                                />
                                <Label for={status}>
                                    {status === "uat" || status === "na"
                                        ? status.toUpperCase()
                                        : status[0].toUpperCase() +
                                          status.slice(1)}
                                </Label>
                            </div>
                        )
                    )}
                </Col>
            </FormGroup>

            {/* Address */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label htmlFor="address">Address:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        inputId="address"
                        name="address"
                        className={styles.input}
                        minLength={1}
                        maxLength={4096}
                        onChange={(e) => handleFormChange(e, setFormData)}
                        value={formData.address}
                    />
                </Col>
            </FormGroup>

            {/* Contact Emails */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label htmlFor="contact_emails">Contact emails:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        inputId="contact_emails"
                        name="contact_emails"
                        className={styles.input}
                        minLength={1}
                        maxLength={250}
                        onChange={(e) => handleFormChange(e, setFormData)}
                        value={formData.contact_emails}
                    />
                </Col>
            </FormGroup>

            {/* Notes */}
            <FormGroup row className={styles.formGroup}>
                <Col sm={2}>
                    <Label htmlFor="notes">Notes:</Label>
                </Col>
                <Col sm={10}>
                    <Input
                        inputId="notes"
                        name="notes"
                        className={styles.input}
                        minLength={1}
                        maxLength={2048}
                        onChange={(e) => handleFormChange(e, setFormData)}
                        value={formData.notes}
                    />
                </Col>
            </FormGroup>
        </Form>
    );

    return (
        <Modal isOpen={activeModal !== ""} toggle={closeModal}>
            <ModalHeader toggle={closeModal}>{headerContent}</ModalHeader>
            <ModalBody>{bodyContent}</ModalBody>
            <ModalFooter
                id="modal_footer"
                style={{ position: "relative", gap: "25px" }}
            >
                {isLoading && (
                    <DimensionSpinner
                        style={{
                            height: "40px",
                            width: "40px",
                            position: "absolute",
                            marginRight: "100px",
                            marginLeft: "auto",
                        }}
                    />
                )}
                {errorMessage && <div className="error">{errorMessage}</div>}
                <Button
                    disabled={isLoading}
                    color="primary"
                    type="submit"
                    form="form"
                >
                    Submit
                </Button>
            </ModalFooter>
        </Modal>
    );
}
